"use client";

import { AnimatedThemeToggler } from "@packages/ui/components/animated-theme-toggler";
import { Button } from "@packages/ui/components/button";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@packages/ui/components/sheet";
import { Skeleton } from "@packages/ui/components/skeleton";
import { UserButton } from "@packages/ui/components/user-button";
import { GitHubIcon } from "@packages/ui/icons/index";
import { authClient } from "@packages/ui/lib/auth-client";
import { cn } from "@packages/ui/lib/utils";
import { Menu } from "lucide-react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

const navItems = [
	{ href: "/tasks", label: "Tasks" },
	{ href: "/leaderboard", label: "Leaderboard" },
] as const;

export function ClawyNav() {
	const session = authClient.useSession();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const [mobileOpen, setMobileOpen] = useState(false);

	// Auto-trigger sign-in when redirected from a protected page
	useEffect(() => {
		if (searchParams.get("signin") && !session.isPending && !session.data) {
			const url = new URL(window.location.href);
			url.searchParams.delete("signin");
			window.history.replaceState({}, "", url.toString());
			authClient.signIn.social({ provider: "github" });
		}
	}, [searchParams, session.isPending, session.data]);

	return (
		<header className="shrink-0 border-b border-border bg-background/80 backdrop-blur-sm">
			<div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 md:px-6">
				{/* Left: logo + nav */}
				<div className="flex items-center gap-8">
					<Link href="/" className="flex items-center gap-2">
						<span className="text-lg font-semibold tracking-tight">Clawy</span>
					</Link>

					<nav className="hidden items-center gap-1 md:flex">
						{navItems.map((item) => {
							const isActive = pathname.startsWith(item.href);
							return (
								<Link
									key={item.href}
									href={item.href}
									className={cn(
										"rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
										isActive
											? "bg-accent text-accent-foreground"
											: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
									)}
								>
									{item.label}
								</Link>
							);
						})}
					</nav>
				</div>

				{/* Right: theme + auth + mobile menu */}
				<div className="flex items-center gap-3">
					<AnimatedThemeToggler />
					{session.isPending ? (
						<Skeleton className="h-8 w-20 rounded-md" />
					) : session.data ? (
						<UserButton />
					) : (
						<Button
							size="sm"
							variant="outline"
							className="gap-1.5"
							onClick={() => {
								authClient.signIn.social({
									provider: "github",
								});
							}}
						>
							<GitHubIcon className="size-3.5" />
							Sign in
						</Button>
					)}

					{/* Mobile hamburger menu */}
					<Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
						<SheetTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								className="md:hidden"
								aria-label="Open navigation menu"
							>
								<Menu className="size-5" />
							</Button>
						</SheetTrigger>
						<SheetContent side="right" className="w-64">
							<SheetHeader>
								<SheetTitle>Navigation</SheetTitle>
							</SheetHeader>
							<nav className="flex flex-col gap-1 px-4">
								{navItems.map((item) => {
									const isActive = pathname.startsWith(item.href);
									return (
										<Link
											key={item.href}
											href={item.href}
											onClick={() => setMobileOpen(false)}
											className={cn(
												"rounded-md px-3 py-2 text-sm font-medium transition-colors",
												isActive
													? "bg-accent text-accent-foreground"
													: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
											)}
										>
											{item.label}
										</Link>
									);
								})}
							</nav>
						</SheetContent>
					</Sheet>
				</div>
			</div>
		</header>
	);
}
